import { I18n } from 'lib/i18n'
import React from 'react'
import ReactTags from 'react-tag-autocomplete'
import MaskedInput from 'react-text-mask'
import { Form, Icon } from 'semantic-ui-react'

export const FormInput = ({
  input,
  meta: { touched, error },
  label,
  noErrorText,
  ...remainingParameters
}) => (
  <Form.Input
    error={error && touched}
    label={
      !noErrorText && error && touched
        ? (label ? label + ' – ' : '') +
          I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
        : label
    }
    value={input.value}
    onChange={(event, comp) => input.onChange(comp.value)}
    onBlur={input.onBlur}
    {...remainingParameters}
  />
)

export const FormInputDelete = ({
  input,
  meta: { touched, error },
  label,
  ...remainingParameters
}) => (
  <Form.Input
    error={error && touched}
    label={
      error && touched
        ? (label ? label + ' – ' : '') +
          I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
        : label
    }
    value={input.value}
    onChange={(event, comp) => input.onChange(comp.value)}
    onBlur={input.onBlur}
    {...remainingParameters}
    icon={<Icon name="delete" link onClick={() => input.onChange('')} />}
  />
)

export const FormInputDate = ({
  input,
  meta: { touched, error },
  placeholder,
  label,
  ...remainingParameters
}) => (
  // TODO: display error states in component (low prio)
  <Form.Input
    error={error && touched}
    label={
      error && touched
        ? (label ? label + ' – ' : '') +
          I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
        : label
    }
    value={input.value}
    onChange={(event, comp) => input.onChange(comp.value)}
    onBlur={input.onBlur}
    children={
      <MaskedInput
        mask={[
          /[0123]/,
          /\d/,
          '.',
          /[01]/,
          /\d/,
          '.',
          /[12]/,
          /\d/,
          /\d/,
          /\d/,
        ]}
        placeholder={placeholder || 'dd.mm.yyyy'}
        value={input.value}
        onChange={(event) => input.onChange(event.target.value)}
      />
    }
    {...remainingParameters}
  />
)

export const FormTextArea = ({
  input,
  meta: { touched, error },
  label,
  ...remainingParameters
}) => (
  <Form.TextArea
    error={error && touched}
    label={
      error && touched
        ? (label ? label + ' – ' : '') +
          I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
        : label
    }
    value={input.value}
    onChange={(event, comp) => input.onChange(comp.value)}
    onBlur={input.onBlur}
    {...remainingParameters}
  />
)

export const FormDropdown = ({
  input,
  meta: { touched, error },
  options,
  label,
  noError,
  ...remainingParameters
}) => (
  <Form.Dropdown
    scrolling
    error={error && touched}
    options={options || []}
    value={input.value || (remainingParameters.multiple === true ? [] : null)}
    label={
      !noError && error && touched
        ? label +
          ' – ' +
          I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
        : label
    }
    onChange={(event, comp) => input.onChange(comp.value)}
    {...remainingParameters}
  />
)

export const FormCheckbox = ({
  input,
  meta: { touched, error },
  ...remainingParameters
}) => (
  <Form.Checkbox
    error={error && touched}
    checked={!!input.value}
    onChange={(event, comp) => input.onChange(comp.checked)}
    {...remainingParameters}
  />
)

export const FormRadio = ({
  input,
  meta: { touched, error },
  label,
  option,
  ...remainingParameters
}) => {
  return (
    <Form.Checkbox
      radio
      error={error && touched}
      checked={input.value === option}
      label={
        error && touched
          ? label +
            ' – ' +
            I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
          : label
      }
      onChange={(event, comp) => input.onChange(option)}
      {...remainingParameters}
    />
  )
}

export const FormToggle = ({
  input,
  meta: { touched, error },
  label,
  option,
  ...remainingParameters
}) => {
  return (
    <Form.Checkbox
      toggle
      error={error && touched}
      checked={input.value === option}
      label={
        error && touched
          ? label +
            ' – ' +
            I18n.t('form.errors.' + error.toLowerCase().replace(' ', '_'))
          : label
      }
      onChange={(event, comp) => input.onChange(option)}
      {...remainingParameters}
    />
  )
}

export const FormTags = ({
  input,
  meta: { touched, error },
  placeholder,
  suggestions,
  ...remainingParameters
}) => {
  let tags = [...input.value]
  const handleDelete = (index) => {
    const newTags = tags.slice(0)
    newTags.splice(index, 1)
    input.onChange(newTags)
  }

  const handleAddition = (tag) => {
    const newTags = [].concat(tags, tag)
    input.onChange(newTags)
  }

  return (
    <ReactTags
      tags={tags}
      placeholder={placeholder}
      suggestions={suggestions}
      handleDelete={handleDelete}
      handleAddition={handleAddition}
      {...remainingParameters}
    />
  )
}

export const validateRequired = (value) => (value ? undefined : 'required')
export const validateNumbers = (value) =>
  value && !/^\d+$/.test(value) ? 'only_numbers' : undefined
export const validateCurrency = (value) =>
  value && !/^\d+(\.\d\d)?$/.test(value) ? 'only_currency' : undefined
export const validateEmail = (value) =>
  value &&
  !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  )
    ? 'email_invalid'
    : undefined
export const validatePhoneNumber = (value) =>
  value && !/^[+0]\d{5,}$/.test(value.replace(/[-\s]/g, ''))
    ? 'phone_number_invalid'
    : undefined
export const validatePassword = (value) =>
  value && value.length < 8 ? 'password_too_short' : undefined
export const validateArrayNotEmpty = (value) =>
  value && value.length === 0 ? 'list_empty' : undefined
export const validateCategoriesRequired = (value) =>
  value && (value.length === 0 || value[0] === -1) ? 'list_empty' : undefined
export const validateRequiredDropdown = (value) =>
  value && value >= 0 ? undefined : 'required'
export const validateLength160 = (value) =>
  value && value.length > 160 ? 'maximum 160 chars' : undefined
export const validatePasswordsMatch = (values) => {
  const errors = {}
  if (values.password !== values.password_confirmation)
    errors.password_confirmation = 'passwords_dont_match'
  return errors
}



// WEBPACK FOOTER //
// ./src/components/shared/Forms/ReduxForm.jsx